import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Button,
  TextAreaGroup,
  SelectableOptionGroup,
  InputFieldGroup,
  Checkbox,
} from "..";
import QuillDisplay from "../QuillDisplay";
import FileDropzoneNew from "../FileDropzoneNew";
import UniversalDocViewer from "../DocViewer/UniversalDocViewer";

const renderTextField = (fieldState, errors, formSubmittedWithTimer) => {
  const { value, onFieldChange, required, label } = fieldState;
  const customErrors = errors.map((error) => {
    if (error.includes("valid")) {
      return `This field must be valid`;
    }
    return error;
  });
  return (
    <div className="form-group">
      <div className="mb-1">
        <QuillDisplay deltaContent={label} />
        {!required && (
          <div className="font-italic text-lightGrey">(optional)</div>
        )}
      </div>
      <TextAreaGroup
        classes={"register_field"}
        value={value}
        onChange={onFieldChange}
        rows={4}
        validationMessages={customErrors}
        validationMessageIsRed
        disabled={formSubmittedWithTimer}
      />
    </div>
  );
};

const renderSingleAnswer = (
  field,
  fieldState,
  errors,
  formSubmittedWithTimer
) => {
  const { value, onFieldChange, options, required, label } = fieldState;
  return (
    <div className="form-group">
      <div className="mb-1">
        <QuillDisplay deltaContent={label} />
        {!required && (
          <div className="font-italic text-lightGrey">(optional)</div>
        )}
      </div>
      <SelectableOptionGroup
        options={options}
        selectedOption={value}
        handleOptionChange={onFieldChange}
        name={field.id}
        validationMessages={errors}
        disabled={formSubmittedWithTimer}
      />
    </div>
  );
};

const renderMultipleAnswers = (
  field,
  fieldState,
  errors,
  formSubmittedWithTimer
) => {
  const { value, onFieldChange, options, required, label } = fieldState;
  return (
    <div className="form-group">
      <div className="mb-1">
        <QuillDisplay deltaContent={label} />
        {!required && (
          <div className="font-italic text-lightGrey">(optional)</div>
        )}
      </div>
      <SelectableOptionGroup
        options={options}
        selectedOption={value}
        handleOptionChange={onFieldChange}
        name={field.id}
        multiple={true}
        validationMessages={errors}
        disabled={formSubmittedWithTimer}
      />
    </div>
  );
};

const renderDateField = (fieldState, errors, formSubmittedWithTimer) => {
  const { value, onFieldChange, required, label } = fieldState;
  return (
    <div className="form-group">
      <div className="mb-1">
        <QuillDisplay deltaContent={label} />
        {!required && (
          <div className="font-italic text-lightGrey">(optional)</div>
        )}
      </div>
      <InputFieldGroup
        type="date"
        value={value}
        onChange={onFieldChange}
        classes="register_field"
        validationMessages={errors}
        disabled={formSubmittedWithTimer}
      />
    </div>
  );
};

const renderNumberField = (fieldState, errors, formSubmittedWithTimer) => {
  const { value, onFieldChange, required, label } = fieldState;
  const customErrors = errors.map((error) => {
    if (error.includes("valid")) {
      return `This field must be valid`;
    }
    return error;
  });
  return (
    <div className="form-group">
      <div className="mb-1">
        <QuillDisplay deltaContent={label} />
        {!required && (
          <div className="font-italic text-lightGrey">(optional)</div>
        )}
      </div>
      <InputFieldGroup
        type="number"
        value={value}
        onChange={onFieldChange}
        classes="register_field w-50"
        validationMessages={customErrors}
        min="0"
        disabled={formSubmittedWithTimer}
      />
    </div>
  );
};

const renderStatementConfirmationField = (
  field,
  fieldState,
  errors,
  formSubmittedWithTimer
) => {
  const { value, onCheckBoxChange, label, required } = fieldState;
  return (
    <Fragment>
      <div className="form-group mt-3 mb-2">
        <Checkbox
          name={field.id}
          isChecked={value}
          onClick={onCheckBoxChange}
          label={
            <>
              <QuillDisplay deltaContent={label} />
              {!required && (
                <div className="font-italic text-lightGrey">(optional)</div>
              )}
            </>
          }
          validationMessages={errors}
          disabled={formSubmittedWithTimer}
        />
      </div>
    </Fragment>
  );
};

const renderReadingDocument = (attachment, downloadAttachmentContent) => {
  return (
    <UniversalDocViewer
      documentUrl={attachment.url}
      filename={attachment.filename}
      type={attachment.type}
      downloadAttachmentContent={downloadAttachmentContent}
    />
  );
};

const renderFileUploadField = (field, fieldState, errors, formSubmitted) => {
  const { value, onFileChange, label, required } = fieldState;
  const [isValidFileType, setIsValidFileType] = useState(true);
  return (
    <div className="form-group">
      <div className="mb-1">
        <QuillDisplay deltaContent={label} />
        {!required && (
          <div className="font-italic text-lightGrey">(optional)</div>
        )}
      </div>
      <FileDropzoneNew
        acceptedFileTypes={".jpg,.jpeg,.png,.pdf,.doc,.docx"}
        handleChange={onFileChange}
        fileStateName={field.id}
        fileParsedToString={value}
        handleInvalidType={() => setIsValidFileType(false)}
        isFileValid={isValidFileType}
        validationMessages={errors}
        imageAsBase64={false}
        fileCategory="document"
        disabled={formSubmitted}
      />
    </div>
  );
};

const renderField = (field, fieldState, errors, formSubmitted) => {
  switch (field.type) {
    case "text_response":
      return renderTextField(fieldState, errors, formSubmitted);
    case "single_answer":
      return renderSingleAnswer(field, fieldState, errors, formSubmitted);
    case "multiple_answers":
      return renderMultipleAnswers(field, fieldState, errors, formSubmitted);
    case "date_response":
      return renderDateField(fieldState, errors, formSubmitted);
    case "number_response":
      return renderNumberField(fieldState, errors, formSubmitted);
    case "statement_confirmation":
      return renderStatementConfirmationField(
        field,
        fieldState,
        errors,
        formSubmitted
      );
    case "file_upload":
      return renderFileUploadField(field, fieldState, errors, formSubmitted);
    default:
      return null;
  }
};

const FormBuilder = ({
  formSubmitted = false,
  id,
  fields,
  description,
  timer,
  attachment,
  downloadAttachmentContent,
  useFieldState,
  handleSubmit,
  handleBack,
  formStyles = "",
  actionStyles = "",
  errors,
}) => {
  const hasTimer = timer.enabled;
  const formSubmittedWithTimer = formSubmitted && hasTimer;
  const [timeRemaining, setTimeRemaining] = useState(
    timer.enabled && !formSubmitted ? timer.value * 60 : null
  );

  const fieldRefs = useRef({});

  useEffect(() => {
    if (timer.enabled && timeRemaining > 0 && !formSubmitted) {
      const interval = setInterval(() => {
        setTimeRemaining((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearInterval(interval);
    }

    if (timer.enabled && timeRemaining === 0 && !formSubmitted) {
      handleSubmit(new Event("submit"), id);
    }

    return undefined;
  }, [timer.enabled, timeRemaining, handleSubmit, id, formSubmitted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(id, fieldRefs);
  };

  return (
    <form
      className={`col-sm-12 col-lg-6 mt-2 mt-lg-4 mb-2 ${formStyles}`}
      onSubmit={handleFormSubmit}
    >
      <div className={`mb-4`}>
        {timer.enabled && timeRemaining > 0 && (
          <div className="timer mb-2">
            <strong>Time Remaining:</strong> {formatTime(timeRemaining)}
          </div>
        )}
        {description && (
          <div className={"mb-2"}>
            <QuillDisplay deltaContent={description} />
          </div>
        )}
        {attachment &&
          renderReadingDocument(attachment, downloadAttachmentContent)}
        {fields.map((field) => {
          const fieldState = useFieldState(id, field);
          const errorsExist = errors[id] && errors[id][field.id];
          return (
            <div
              key={field.id}
              className={"mb-2"}
              ref={(el) => {
                fieldRefs.current[field.id] = el;
              }}
            >
              {renderField(
                field,
                fieldState,
                errorsExist,
                formSubmittedWithTimer
              )}
            </div>
          );
        })}
      </div>
      <div className={`mt-lg-auto mb-lg-2 ${actionStyles}`}>
        <Button
          classes="btn btn-outline-info text-dark mr-1"
          onClick={handleBack}
        >
          Back
        </Button>
        <Button classes="btn btn-primary btn-block">Next</Button>
      </div>
    </form>
  );
};

export default FormBuilder;
